import React, { useCallback } from 'react';
import { Box, MenuItem, Select, Typography } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  setCctvGpuAllocation,
  type GpuAllocationStatus,
  resolveCctvGpuIndex,
} from '../../services/gpuAllocationService';

type Props = {
  poolId: number;
  cctvId: number;
  gpuStatus: GpuAllocationStatus | null;
  manualMode: boolean;
  /** 서버 AI 추론이 돌아가야 GPU 자동 할당이 채워짐 */
  serverInferenceActive?: boolean;
  onGpuChanged?: (gpuIndex: number) => void;
};

const CctvGpuSelector: React.FC<Props> = ({
  poolId,
  cctvId,
  gpuStatus,
  manualMode,
  serverInferenceActive = true,
  onGpuChanged,
}) => {
  const deviceCount = gpuStatus?.device_count ?? 0;
  const gpuIndex = resolveCctvGpuIndex(gpuStatus, poolId, cctvId);

  const handleChange = useCallback(
    async (e: SelectChangeEvent<string>) => {
      const next = Number(e.target.value);
      if (!Number.isFinite(next)) return;
      try {
        await setCctvGpuAllocation(poolId, cctvId, next);
        onGpuChanged?.(next);
      } catch (err) {
        console.warn('CCTV GPU 변경 실패', err);
      }
    },
    [poolId, cctvId, onGpuChanged],
  );

  if (gpuStatus === null) {
    return null;
  }

  if (!gpuStatus.enabled) {
    return null;
  }

  let label: string;
  if (deviceCount <= 0) {
    label = 'GPU 없음';
  } else if (gpuIndex != null) {
    label = `GPU ${gpuIndex}`;
  } else if (manualMode) {
    label = 'GPU —';
  } else if (!serverInferenceActive) {
    label = 'AI 꺼짐';
  } else {
    label = 'GPU 대기';
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        left: 6,
        bottom: 6,
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.75,
        py: 0.25,
        borderRadius: 0.5,
        bgcolor: 'rgba(0,0,0,0.65)',
        pointerEvents: 'auto',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {manualMode && deviceCount > 0 ? (
        <Select
          size="small"
          variant="standard"
          value={gpuIndex != null ? String(gpuIndex) : ''}
          displayEmpty
          onChange={handleChange}
          sx={{
            color: 'rgba(255,255,255,0.92)',
            fontSize: '0.65rem',
            minWidth: 56,
            '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.7)' },
            '&:before, &:after': { display: 'none' },
          }}
        >
          {Array.from({ length: deviceCount }, (_, i) => (
            <MenuItem key={i} value={String(i)} dense sx={{ fontSize: '0.75rem' }}>
              GPU {i}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Typography
          component="span"
          sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.9)', lineHeight: 1.2 }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
};

export default CctvGpuSelector;
